import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, animate, useInView, AnimatePresence } from "framer-motion";
import api, { apiErr, fmtMoney, fileUrl } from "../lib/api";
import { getCache, setCache, hasCache } from "../lib/swr";
import { useAuth } from "../context/AuthContext";
import { Loader2, ArrowRight, ChevronRight, Globe2, HandHeart, HandCoins, Heart, BookOpen, Map, Users, Flame, Sparkles, ExternalLink, MapPin, Bell, X, Check, UserPlus } from "lucide-react";
import TripCountdown from "./TripCountdown";
import TripGlobe from "./TripGlobe";
import DailyMission from "./DailyMission";
import { getTripStatus } from "../lib/tripStatus";
import { flagEmoji } from "../lib/flags";
import { enablePush, pushEnabled } from "../lib/push";
import GiveDialog from "./GiveDialog";
import { openGive as openGiveLink } from "../lib/give";
import IntentPicker from "./IntentPicker";
import NewJourneyCard from "./NewJourneyCard";
import JourneyCompleteCard from "./JourneyCompleteCard";

const NAVY = "#0d2340";
const GOLD = "#E0A73A";
const BLUE = "#2563eb";
const TEAL = "#0E7C86";
const GREEN = "#1E8A5B";
const cleanName = (n) => (n || "").replace(/[^\p{L}\s,]/gu, "").split(",").pop().trim();
const AV_COLORS = ["#2563eb", "#E0A73A", "#1E8A5B", "#0E7C86", "#C2557A", "#7C4DB8"];
const initials = (name) => (name || "?").trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase();

export default function PartnerHome() {
  const { user, org, config, refreshMe } = useAuth();
  const navigate = useNavigate();
  const [d, setD] = useState(() => getCache("you:data") || null);
  const [loading, setLoading] = useState(() => !hasCache("you:data"));
  const [giveOpen, setGiveOpen] = useState(false);
  const [intentDismissed, setIntentDismissed] = useState(false);
  const [upgrading, setUpgrading] = useState("");
  const [celebrate, setCelebrate] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await Promise.all([
          api.get("/budget/summary"), api.get("/dispatches"), api.get("/itinerary/countries"),
          api.get("/itinerary/entries"), api.get("/you/summary"), api.get("/team").catch(() => ({ data: [] })),
        ]);
        const payload = { budget: r[0].data, dispatches: r[1].data, countries: r[2].data, entries: r[3].data, you: r[4].data, team: r[5].data };
        setD(payload); setCache("you:data", payload);
      } catch (e) { import("sonner").then(({ toast }) => toast.error(apiErr(e))); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin" style={{ color: GOLD }} /></div>;

  const cur = d?.budget?.currency || "USD";
  const goal = d?.budget?.goal_cents || 0;
  const raised = d?.budget?.raised_cents || 0;
  const pct = goal ? Math.min(100, Math.round((raised / goal) * 100)) : 0;
  const you = d?.you || {};
  const fin = you.finance || {};
  const comm = you.community || {};
  const firstName = (user?.name || "").split(" ")[0] || "Friend";
  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
  const latest = d?.dispatches?.[0];
  const team = (d?.team || []).filter((t) => t.show_on_team !== false);
  const cover = org?.cover_file_id ? fileUrl(`/api/files/${org.cover_file_id}`, 800) : "/assets/journey-bg.jpg";
  const giveUrl = config?.donate_url || (org?.slug === "reset" ? "https://akop.live/give" : "");

  const origin = config?.origin_lat != null && config?.origin_lng != null
    ? { lat: config.origin_lat, lng: config.origin_lng, country: cleanName(config.origin_country), city: config.origin_city } : null;
  const tripStatus = getTripStatus(config, d?.countries || [], origin);
  const departEntry = (d?.entries || []).filter((e) => e.entry_type === "flight" && e.date)
    .sort((a, b) => `${a.date}T${a.time || "00:00"}`.localeCompare(`${b.date}T${b.time || "00:00"}`))[0];
  const departTarget = departEntry
    ? `${departEntry.date}T${departEntry.time ? (departEntry.time.length === 5 ? departEntry.time + ":00" : departEntry.time) : "00:00:00"}`
    : (config?.start_date ? `${config.start_date}T00:00:00` : null);
  const globeMarkers = (d?.countries || []).filter((c) => typeof c.lat === "number" && typeof c.lng === "number")
    .map((c) => ({ lat: c.lat, lng: c.lng, current: c.id === tripStatus.currentId, name: cleanName(c.name) }));
  const flags = (d?.countries || []).map((c) => ({ name: cleanName(c.name), flag: flagEmoji(c.name) })).filter((f) => f.flag);

  const ways = user?.partnership_ways || [];
  const hasPrayer = ways.includes("prayer");
  const hasFinancial = ways.includes("financial");
  const isFollower = ways.includes("follow") && !hasPrayer && !hasFinancial;

  const addWay = async (way, openGive) => {
    const current = user?.partnership_ways || [];
    if (current.includes(way)) { if (openGive) { if (giveUrl) openGiveLink(giveUrl); else setGiveOpen(true); } return; }
    setUpgrading(way);
    try {
      await api.put("/me/partnership-ways", { partnership_ways: [...current.filter((w) => w !== "follow"), way] });
      await refreshMe();
      setCelebrate(true);
      setTimeout(() => setCelebrate(false), 1700);
      import("sonner").then(({ toast }) => toast.success(way === "prayer" ? "Thank you for praying with us 🙏" : "Thank you for coming alongside us 💛"));
      if (openGive) { if (giveUrl) openGiveLink(giveUrl); else setGiveOpen(true); }
    } catch (e) { import("sonner").then(({ toast }) => toast.error(apiErr(e))); }
    finally { setUpgrading(""); }
  };

  return (
    <div className="-mx-4 -mt-4" style={{ "--tenant-accent": GOLD }} data-testid="partner-home">
      <Celebration show={celebrate} />
      {/* 1. Hero — sunflower photo full-bleed, cream gradient keeps the text crisp */}
      <div className="relative overflow-hidden" style={{ background: "#F7F4EE" }} data-testid="you-hero">
        <div className="absolute inset-0" style={{ backgroundImage: `url(${cover})`, backgroundSize: "cover", backgroundPosition: "center 32%" }} />
        <div className="absolute inset-0" style={{ background: "linear-gradient(105deg, #F7F4EE 0%, #F7F4EE 44%, rgba(247,244,238,0.82) 60%, rgba(247,244,238,0) 88%)" }} />
        <div className="relative px-4 pt-4 pb-7">
          <span className="inline-block font-heading font-extrabold text-lg tracking-[0.14em] pb-1 border-b-2" style={{ color: NAVY, borderColor: GOLD }}>YOU</span>
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="mt-4 max-w-[66%]">
            <h1 className="font-heading font-extrabold text-[28px] leading-[1.12]" style={{ color: NAVY }} data-testid="you-greeting">{greeting}, {firstName}! <span className="inline-block">👋</span></h1>
            <p className="text-sm leading-relaxed mt-2.5" style={{ color: "#3C4C60" }}>Thank you for walking this journey with us — you're part of what God is doing.</p>
            {hasFinancial && (
              <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.35, type: "spring", stiffness: 300, damping: 18 }}
                className="mt-3 inline-flex items-center gap-1.5 rounded-full pl-2 pr-3 h-8 text-xs font-bold shadow-sm" data-testid="financial-partner-badge"
                style={{ background: "linear-gradient(135deg,#1E8A5B,#0E7C86)", color: "#fff" }}>
                <span className="h-5 w-5 rounded-full bg-white/25 flex items-center justify-center"><Check className="h-3 w-3" /></span>
                You're a financial partner
              </motion.div>
            )}
            {fin.giving_streak_months >= 2 && (
              <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
                className="mt-2 flex items-center gap-1.5 text-xs font-bold" data-testid="give-streak-badge" style={{ color: "#B87309" }}>
                <span className="text-sm">💛</span> {fin.giving_streak_months} months faithful
              </motion.div>
            )}
          </motion.div>
        </div>
      </div>

      <div className="px-4 pt-5 relative space-y-7 pb-6">
        <PushPrompt />

        <JourneyCompleteCard navy={NAVY} gold={GOLD} />
        <NewJourneyCard navy={NAVY} gold={GOLD} />

        {/* One-time intent prompt for partners who never chose how they walk with us */}
        {user?.role === "partner" && !user?.intent_set && !intentDismissed && (
          <Reveal>
            <div className="rounded-3xl px-5 py-5 relative overflow-hidden" style={{ background: NAVY }} data-testid="intent-prompt-card">
              <div className="text-[11px] uppercase tracking-[0.2em] font-semibold" style={{ color: GOLD }}>A quick hello</div>
              <h3 className="font-heading font-extrabold text-2xl text-white mt-1.5 leading-tight">How would you like to walk with us?</h3>
              <p className="text-sm text-white/70 mt-1.5 mb-4">Let us know how you'd love to be part of the journey. You can change it anytime.</p>
              <IntentPicker
                initial={user?.partnership_ways || []}
                gold={GOLD}
                dark
                onDone={async () => { await refreshMe(); import("sonner").then(({ toast }) => toast.success("Thank you — that means so much!")); }}
              />
              <button type="button" data-testid="intent-dismiss-btn" onClick={() => setIntentDismissed(true)} className="w-full text-center text-xs text-white/50 hover:text-white/80 mt-3">Maybe later</button>
            </div>
          </Reveal>
        )}

        {/* Follower nudge — invite a follow-only partner to pray or give in one tap */}
        {isFollower && (
          <Reveal>
            <div className="rounded-3xl px-5 py-5 relative overflow-hidden" style={{ background: "#F7F4EE", border: `1px solid ${GOLD}44` }} data-testid="follower-nudge-card">
              <div className="text-[11px] uppercase tracking-[0.2em] font-semibold" style={{ color: TEAL }}>You're following along</div>
              <h3 className="font-heading font-extrabold text-2xl mt-1.5 leading-tight" style={{ color: NAVY }}>Want to walk a little closer?</h3>
              <p className="text-sm leading-relaxed mt-1.5" style={{ color: "#4A5D73" }}>Your encouragement already means so much. If you feel led, you can also pray with us or come alongside us financially — one tap.</p>
              <div className="grid grid-cols-2 gap-2.5 mt-4">
                <button data-testid="nudge-pray-btn" disabled={upgrading === "prayer"} onClick={() => addWay("prayer")}
                  className="rounded-full h-12 flex items-center justify-center gap-2 font-semibold text-white transition-transform active:scale-[0.98]" style={{ background: NAVY }}>
                  {upgrading === "prayer" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><HandHeart className="h-4 w-4" /> I'll pray too</>}
                </button>
                <button data-testid="nudge-give-btn" disabled={upgrading === "financial"} onClick={() => addWay("financial", true)}
                  className="rounded-full h-12 flex items-center justify-center gap-2 font-semibold text-white transition-transform active:scale-[0.98]" style={{ background: GREEN }}>
                  {upgrading === "financial" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><HandCoins className="h-4 w-4" /> I'll give too</>}
                </button>
              </div>
            </div>
          </Reveal>
        )}

        {/* How you partner */}
        {(user?.partnership_ways || []).length > 0 && (
          <Reveal>
            <div className="rounded-3xl px-6 py-5 relative overflow-hidden" style={{ background: "#F7F4EE", border: `1px solid ${GOLD}33` }} data-testid="you-partnership-ways">
              <div className="text-[11px] uppercase tracking-[0.2em] font-semibold" style={{ color: GOLD }}>How you partner</div>
              <div className="flex flex-wrap gap-2 mt-3">
                {(user.partnership_ways || []).includes("prayer") && (
                  <span className="inline-flex items-center gap-1.5 rounded-full px-4 h-9 text-sm font-semibold text-white" style={{ background: NAVY }}>
                    <HandHeart className="h-4 w-4" /> Prayer
                  </span>
                )}
                {(user.partnership_ways || []).includes("financial") && (
                  <span className="inline-flex items-center gap-1.5 rounded-full px-4 h-9 text-sm font-semibold text-white" style={{ background: GREEN }}>
                    <HandCoins className="h-4 w-4" /> Financial
                  </span>
                )}
                {(user.partnership_ways || []).includes("follow") && !(user.partnership_ways || []).some((w) => w === "prayer" || w === "financial") && (
                  <span className="inline-flex items-center gap-1.5 rounded-full px-4 h-9 text-sm font-semibold text-white" style={{ background: TEAL }}>
                    <Users className="h-4 w-4" /> Following
                  </span>
                )}
              </div>
              <p className="text-sm leading-relaxed mt-3" style={{ color: "#4A5D73" }}>Thank you for standing with us in this way. It matters more than you know.</p>
            </div>
          </Reveal>
        )}


        {/* 2. Countdown */}
        {departTarget && (
          <Reveal>
            <div className="rounded-3xl px-6 py-4 text-white shadow-[0_14px_40px_rgba(13,35,64,0.28)] relative overflow-hidden" style={{ background: `linear-gradient(135deg,#0d2340 0%,#14365f 55%,#1f5290 100%)` }} data-testid="you-countdown">
              <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none" aria-hidden>
                <svg width="66" height="72" viewBox="0 0 66 72" fill="none">
                  <path d="M6 66 C 34 62, 18 34, 48 28" stroke="#E0A73A" strokeWidth="2" strokeDasharray="3 5" strokeLinecap="round" fill="none" opacity="0.85" />
                </svg>
                <MapPin className="absolute -top-1 right-1 h-7 w-7" style={{ color: GOLD }} fill={GOLD} />
              </div>
              <TripCountdown startDate={departTarget} endDate={config?.end_date} variant="plain" label="We begin the journey" />
            </div>
          </Reveal>
        )}

        {/* 3. Your Mission Today (daily prayer) */}
        <Reveal><DailyMission isAdmin={false} theme="warm" /></Reveal>

        {/* 3a. Latest dispatch — surfaced right under today's mission */}
        {latest && (
          <Reveal>
            <motion.button whileTap={{ scale: 0.99 }} data-testid="you-dispatch" onClick={() => navigate("/app/feed")} className="w-full text-left bg-white rounded-3xl overflow-hidden shadow-[0_6px_28px_rgba(13,35,64,0.08)]">
              {latest.media?.length > 0 && <img src={fileUrl(latest.media[0], 800)} alt="" className="w-full h-48 object-cover" />}
              <div className="p-6">
                <div className="text-xs uppercase tracking-[0.15em] font-semibold" style={{ color: GOLD }}>Latest dispatch</div>
                <h3 className="font-heading font-bold text-2xl leading-snug mt-1" style={{ color: NAVY }}>{latest.title}</h3>
                <p className="text-base mt-2 line-clamp-2" style={{ color: "#4A5D73" }}>{latest.body}</p>
                <div className="text-sm mt-3 flex items-center gap-1 font-semibold" style={{ color: GOLD }}>Read more <ChevronRight className="h-4 w-4" /></div>
              </div>
            </motion.button>
          </Reveal>
        )}

        {/* 3b. Scripture — soft-blue card */}
        <Reveal>
          <div className="rounded-3xl px-6 py-6 relative overflow-hidden" style={{ background: "linear-gradient(135deg,#E8EEF9,#DBE5F4)" }} data-testid="you-scripture">
            <img src="/assets/journey-bg.jpg" alt="" className="absolute right-0 bottom-0 w-1/2 h-full object-cover opacity-25 pointer-events-none" style={{ WebkitMaskImage: "linear-gradient(to left,#000,transparent 92%)", maskImage: "linear-gradient(to left,#000,transparent 92%)" }} />
            <div className="relative flex gap-3 items-start">
              <span className="font-heading font-black text-5xl leading-[0.8]" style={{ color: "#B9C6DE" }}>&ldquo;</span>
              <div className="flex-1 min-w-0">
                <p className="font-heading font-bold text-xl leading-snug" style={{ color: NAVY }}>How can anyone go unless someone is sent?</p>
                <div className="text-sm font-bold tracking-wide mt-2" style={{ color: BLUE }}>ROMANS 10:15</div>
                <div className="text-[11px] mt-1 italic" style={{ color: "#8A97A6" }}>Paraphrased for reflection</div>
              </div>
              {comm.follower_count > 0 && (
                <div className="shrink-0 flex flex-col items-center" data-testid="you-total-partners">
                  <span className="h-16 w-16 rounded-full flex items-center justify-center border-[3px]" style={{ borderColor: GOLD }}>
                    <CountUp value={comm.follower_count} className="font-heading font-black text-2xl leading-none" style={{ color: NAVY }} />
                  </span>
                  <span className="text-[9px] font-bold uppercase tracking-[0.1em] mt-1.5" style={{ color: BLUE }}>Partners</span>
                </div>
              )}
            </div>
            {comm.follower_count > 0 && (
              <div data-testid="you-journey-count" className="relative mt-5 pt-5 border-t border-[#0d2340]/10">
                <div className="grid grid-cols-3 divide-x divide-[#0d2340]/10">
                  {[
                    { icon: HandHeart, label: "Prayer Partners", value: comm.prayer_partners || 0, tid: "stat-prayer" },
                    { icon: HandCoins, label: "Financial Partners", value: comm.financial_partners || 0, tid: "stat-financial" },
                    { icon: Users, label: "Following", value: comm.following || 0, tid: "stat-following" },
                  ].map((s) => (
                    <div key={s.tid} data-testid={`you-${s.tid}`} className="px-2 first:pl-0 last:pr-0 text-center">
                      <s.icon className="h-4 w-4 mx-auto mb-1.5" style={{ color: GOLD }} />
                      <CountUp value={s.value} className="font-heading font-black text-3xl leading-none block" style={{ color: NAVY }} />
                      <div className="text-[10px] font-bold uppercase tracking-[0.08em] mt-1.5 leading-tight" style={{ color: BLUE }}>{s.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </Reveal>

        {/* 3b. Community pulse */}
        {(comm.prayed_today > 0 || comm.follower_count > 0) && (
          <Reveal>
            <div data-testid="you-community" className="rounded-3xl p-6 text-white shadow-[0_10px_34px_rgba(37,99,235,0.28)] relative overflow-hidden" style={{ background: `linear-gradient(135deg,${TEAL},${BLUE})` }}>
              <div className="absolute -bottom-12 -left-8 h-36 w-36 rounded-full bg-white/10" />
              <div className="relative">
                <div className="text-[11px] uppercase tracking-[0.2em] font-semibold flex items-center gap-1.5" style={{ color: GOLD }}><Users className="h-3.5 w-3.5" /> Praying together</div>
                <div className="flex items-end gap-2 mt-2">
                  <CountUp value={comm.prayed_today || 0} className="font-heading font-extrabold text-5xl leading-none" />
                  <div className="text-base font-medium pb-1 text-white/90">praying with you today</div>
                </div>
                {comm.prayed_today_names?.length > 0 && (
                  <div className="flex items-center mt-4">
                    <div className="flex -space-x-2">
                      {comm.prayed_today_names.slice(0, 5).map((n, i) => (
                        <div key={`${n}-${i}`} className="rounded-full flex items-center justify-center text-white text-[11px] font-bold ring-2 ring-white/40" style={{ width: 30, height: 30, background: AV_COLORS[i % AV_COLORS.length] }}>{initials(n)}</div>
                      ))}
                    </div>
                    {comm.prayed_today > 5 && <span className="text-sm text-white/80 ml-3">+{comm.prayed_today - 5} more</span>}
                  </div>
                )}
                {comm.recent?.length > 0 && (
                  <div className="mt-4 space-y-1.5">
                    {comm.recent.slice(0, 3).map((a, i) => (
                      <div key={`${a.name}-${a.at}-${i}`} className="flex items-center gap-2 text-sm text-white/90">
                        <span className="h-1.5 w-1.5 rounded-full bg-white/70 shrink-0" />
                        <span className="truncate"><strong className="font-semibold">{(a.name || "A partner").split(" ")[0]}</strong> {a.action === "encouraged" ? "left an encouragement" : "prayed"}</span>
                      </div>
                    ))}
                  </div>
                )}
                {comm.new_partners?.length > 0 && (
                  <div data-testid="you-new-partners" className="mt-4 pt-4 border-t border-white/15">
                    <div className="text-[11px] uppercase tracking-[0.2em] font-semibold flex items-center gap-1.5" style={{ color: GOLD }}><UserPlus className="h-3.5 w-3.5" /> Newly joined</div>
                    <div className="mt-2 space-y-1.5">
                      {comm.new_partners.slice(0, 3).map((p, i) => (
                        <div key={`${p.name}-${p.at}-${i}`} className="flex items-center gap-2 text-sm text-white/90">
                          <span className="h-1.5 w-1.5 rounded-full bg-white/70 shrink-0" />
                          <span className="truncate"><strong className="font-semibold">{(p.name || "A new partner").split(" ")[0]}</strong> joined the journey</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Reveal>
        )}

        {/* 4. Journey map — globe faded into the card */}
        {(globeMarkers.length > 0 || origin) && (
          <Reveal>
            <div className="rounded-3xl px-5 pt-5 pb-5 shadow-[0_8px_30px_rgba(37,99,235,0.10)] relative overflow-hidden" style={{ background: "linear-gradient(180deg,#EEF4FF,#FFFFFF)" }} data-testid="you-journey">
              <div className="flex items-start justify-between">
                <div className="text-xs uppercase tracking-[0.15em] font-semibold" style={{ color: BLUE }}><Globe2 className="h-4 w-4 inline mr-1" /> The Journey</div>
                <div className="text-xs" style={{ color: "#8A97A6" }}>Follow the mission as it unfolds.</div>
              </div>
              <div className="flex justify-center mt-1" style={{ maskImage: "linear-gradient(to bottom, #000 38%, transparent 70%)", WebkitMaskImage: "linear-gradient(to bottom, #000 38%, transparent 70%)" }}>
                <TripGlobe markers={globeMarkers} origin={origin} size={250} />
              </div>
              <div className="relative -mt-20 text-center">
                <div className="text-base font-semibold" style={{ color: NAVY }}><span className="font-heading font-extrabold">{globeMarkers.length}</span> countries on the route</div>
                {flags.length > 0 && <div className="flex flex-wrap justify-center gap-2 mt-2 text-2xl">{flags.map((f, i) => <span key={`${f.name}-${i}`} title={f.name}>{f.flag}</span>)}</div>}
                <button data-testid="you-itinerary-btn" onClick={() => navigate("/app/itinerary")} className="mt-4 mx-auto w-full max-w-[300px] rounded-full h-12 font-semibold text-white flex items-center justify-center gap-2 transition-transform active:scale-[0.98]" style={{ background: `linear-gradient(135deg,${BLUE},${TEAL})` }}>View full itinerary <ArrowRight className="h-4 w-4" /></button>
              </div>
            </div>
          </Reveal>
        )}

        {/* 7. Mission progress */}
        {goal > 0 && (
          <Reveal>
            <div className="bg-white rounded-3xl p-6 shadow-[0_4px_24px_rgba(13,35,64,0.05)]" data-testid="you-progress">
              <div className="text-xs uppercase tracking-[0.15em] font-semibold" style={{ color: "#4A5D73" }}>Mission progress</div>
              <div className="flex items-end justify-between mt-2">
                <div className="font-heading font-extrabold text-3xl" style={{ color: NAVY }}><CountUp value={pct} />% funded</div>
                <div className="text-sm" style={{ color: "#4A5D73" }}>{fmtMoney(raised, cur)} of {fmtMoney(goal, cur)}</div>
              </div>
              <div className="mt-3 h-2.5 rounded-full overflow-hidden" style={{ background: "rgba(13,35,64,0.08)" }}>
                <motion.div className="h-full rounded-full" initial={{ width: 0 }} whileInView={{ width: `${pct}%` }} viewport={{ once: true }} transition={{ duration: 1.1, ease: "easeOut" }} style={{ background: `linear-gradient(90deg,${GOLD},#F0C265)` }} />
              </div>
              <p className="text-sm mt-3" style={{ color: "#4A5D73" }}>Together, we're making this journey possible.</p>
            </div>
          </Reveal>
        )}

        {/* 8. Your Impact */}
        <Reveal>
          <div className="bg-white rounded-3xl p-6 shadow-[0_4px_24px_rgba(13,35,64,0.05)]" data-testid="you-impact">
            <h2 className="font-heading font-bold text-2xl" style={{ color: NAVY }}>Your Impact</h2>
            <p className="text-sm mt-0.5" style={{ color: "#4A5D73" }}>Because of your partnership…</p>
            <div className="grid grid-cols-3 gap-3 mt-5 text-center">
              <Metric icon={Globe2} tint="rgba(37,99,235,0.10)" color={BLUE} num={you.impact?.countries ?? 0} label="Countries we're reaching" />
              <Metric icon={HandHeart} tint="rgba(224,167,58,0.15)" color="#B07A17" num={you.impact?.times_prayed ?? 0} label="Times you've prayed" />
              <Metric icon={Heart} tint="rgba(30,138,91,0.12)" color={GREEN} num={you.impact?.encouragements ?? 0} label="Encouragements shared" />
            </div>
          </div>
        </Reveal>

        {/* 9 + 10. Financial Partnership + Prayer Journal — 2-up */}
        <Reveal>
          <div className="grid grid-cols-2 gap-3">
            <motion.button whileTap={{ scale: 0.98 }} data-testid="you-finance"
              onClick={() => { if (fin.total_invested_cents > 0) navigate("/app/impact"); else if (giveUrl) openGiveLink(giveUrl); else navigate("/app/impact"); }}
              className="text-left bg-white rounded-3xl p-5 shadow-[0_4px_24px_rgba(13,35,64,0.05)] flex flex-col">
              <div className="h-9 w-9 rounded-full flex items-center justify-center mb-3" style={{ background: "rgba(30,138,91,0.12)" }}><HandCoins className="h-5 w-5" style={{ color: GREEN }} /></div>
              <div className="text-[11px] uppercase tracking-wide font-semibold" style={{ color: "#4A5D73" }}>Your giving</div>
              {fin.total_invested_cents > 0 ? (
                <>
                  <div className="font-heading font-extrabold text-2xl leading-tight mt-0.5" style={{ color: GREEN }}>{fmtMoney(fin.total_invested_cents, cur)}</div>
                  <div className="text-xs mt-auto pt-2 flex items-center gap-1 font-semibold" style={{ color: GOLD }}>Full history <ChevronRight className="h-3.5 w-3.5" /></div>
                </>
              ) : (
                <>
                  <div className="font-heading font-bold text-lg leading-tight mt-0.5" style={{ color: NAVY }}>Partner in giving</div>
                  <div className="text-xs mt-auto pt-2 flex items-center gap-1 font-semibold" style={{ color: GOLD }}>{giveUrl ? "Give now" : "Learn more"} <ChevronRight className="h-3.5 w-3.5" /></div>
                </>
              )}
            </motion.button>

            <motion.button whileTap={{ scale: 0.98 }} data-testid="you-journal" onClick={() => navigate("/app/prayer")}
              className="text-left bg-white rounded-3xl p-5 shadow-[0_4px_24px_rgba(13,35,64,0.05)] flex flex-col">
              <div className="flex items-center justify-between mb-3">
                <div className="h-9 w-9 rounded-full flex items-center justify-center" style={{ background: "rgba(224,167,58,0.15)" }}><BookOpen className="h-5 w-5" style={{ color: "#B07A17" }} /></div>
                {you.streak > 0 && <span className="flex items-center gap-1 text-xs font-bold rounded-full px-2 py-0.5" style={{ background: "rgba(224,167,58,0.15)", color: "#B07A17" }}><Flame className="h-3 w-3" /> {you.streak}</span>}
              </div>
              <div className="text-[11px] uppercase tracking-wide font-semibold" style={{ color: "#4A5D73" }}>Prayer journal</div>
              {you.journal?.last ? (
                <p className="text-sm mt-0.5 italic line-clamp-2 leading-snug" style={{ color: NAVY }}>"{you.journal.last.text}"</p>
              ) : (
                <div className="font-heading font-bold text-lg leading-tight mt-0.5" style={{ color: NAVY }}>Start today</div>
              )}
              <div className="text-xs mt-auto pt-2 flex items-center gap-1 font-semibold" style={{ color: GOLD }}>Open journal <ChevronRight className="h-3.5 w-3.5" /></div>
            </motion.button>
          </div>
        </Reveal>

        {/* 3c. Partnership Manifesto — moved below giving & prayer journal */}
        <Reveal>
          <a href="https://akop.live/manifesto" target="_blank" rel="noreferrer" data-testid="you-manifesto"
            className="block rounded-3xl p-6 relative overflow-hidden shadow-[0_10px_34px_rgba(13,35,64,0.25)]" style={{ background: `linear-gradient(135deg,${NAVY},#0A1B33)` }}>
            <div className="absolute -top-10 -right-8 h-36 w-36 rounded-full pointer-events-none" style={{ background: `radial-gradient(circle, ${GOLD}55, transparent 70%)` }} />
            <div className="relative">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-semibold" style={{ color: GOLD }}>
                <BookOpen className="h-3.5 w-3.5" /> Our commitment to you
              </div>
              <h2 className="font-heading font-extrabold text-3xl leading-tight mt-2" style={{ color: "#F7F4EE" }}>The Partnership Manifesto</h2>
              <p className="text-sm leading-relaxed mt-2 text-white/75">What we believe about mission, partnership, and walking together. This is the ground we stand on — the way we hope to walk with you.</p>
              <span className="inline-flex items-center gap-1.5 mt-4 font-semibold text-sm" style={{ color: GOLD }}>Read the Manifesto <ArrowRight className="h-4 w-4" /></span>
            </div>
          </a>
        </Reveal>

        {/* 10. Quick Actions */}
        <Reveal>
          <div className="space-y-3" data-testid="you-quick-actions">
            <QuickRow icon={Map} tint="rgba(37,99,235,0.10)" color={BLUE} title="View Our Journey" desc="See today's route and itinerary." onClick={() => navigate("/app/itinerary")} testid="qa-journey" />
            <QuickRow icon={Users} tint="rgba(14,124,134,0.12)" color={TEAL} title="Meet Our Team" desc={team.length > 0 ? `${team.length} serving on this mission.` : "Learn about the people serving."} onClick={() => navigate("/app/team")} testid="qa-team" faces={team} />
            <QuickRow icon={HandHeart} tint="rgba(224,167,58,0.15)" color="#B07A17" title="Prayer Wall" desc="Pray over requests and share encouragement." onClick={() => navigate("/app/prayer")} testid="qa-prayer" />
            {org?.website && <QuickRow icon={ExternalLink} tint="rgba(13,35,64,0.08)" color={NAVY} title="Visit Website" desc={`Learn more about ${org?.org_name || "us"}.`} onClick={() => window.open(org.website.startsWith("http") ? org.website : `https://${org.website}`, "_blank")} testid="qa-website" />}
          </div>
        </Reveal>

        {/* Thank you */}
        <Reveal>
          <div className="text-center px-4 py-6">
            <Sparkles className="h-6 w-6 mx-auto mb-3" style={{ color: GOLD }} />
            <p className="font-heading font-bold text-2xl leading-snug" style={{ color: NAVY }}>Thank you for being part of this story.</p>
            <p className="text-base mt-2" style={{ color: "#4A5D73" }}>Your prayers. Your encouragement. Your support. Together, lives are being changed.</p>
          </div>
        </Reveal>
      </div>
      <GiveDialog open={giveOpen} onOpenChange={setGiveOpen} donateUrl={config?.donate_url} donateLabel={config?.donate_label} orgSlug={org?.slug} />
    </div>
  );
}

function Reveal({ children }) {
  return (
    <motion.div initial={{ opacity: 0, y: 22 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}>
      {children}
    </motion.div>
  );
}

function PushPrompt() {
  const [show, setShow] = useState(!pushEnabled());
  const [busy, setBusy] = useState(false);
  if (!show) return null;
  const turnOn = async () => {
    setBusy(true);
    try {
      await enablePush();
      import("sonner").then(({ toast }) => toast.success("Notifications on — you won't miss a thing 🙏"));
      setShow(false);
    } catch (e) {
      import("sonner").then(({ toast }) => toast.error(e.message || "Couldn't enable notifications"));
    } finally { setBusy(false); }
  };
  return (
    <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} data-testid="push-prompt"
      className="rounded-3xl p-5 relative overflow-hidden" style={{ background: "linear-gradient(135deg,#FDF3E7,#FBE6CE)" }}>
      <button data-testid="push-prompt-dismiss" onClick={() => setShow(false)} className="absolute top-3 right-3 h-7 w-7 rounded-full flex items-center justify-center text-[#8A6A2F] hover:bg-black/5"><X className="h-4 w-4" /></button>
      <div className="flex items-start gap-3 pr-6">
        <div className="h-11 w-11 rounded-2xl bg-white flex items-center justify-center shrink-0 shadow-sm"><Bell className="h-5 w-5" style={{ color: GOLD }} /></div>
        <div className="flex-1 min-w-0">
          <div className="font-heading font-bold text-lg leading-tight" style={{ color: NAVY }}>Never miss an urgent prayer</div>
          <p className="text-sm mt-1" style={{ color: "#6B5A3A" }}>Turn on notifications for prayer requests, new dispatches and milestones — the moment they happen.</p>
          <div className="flex items-center gap-3 mt-3">
            <button data-testid="push-prompt-enable" onClick={turnOn} disabled={busy}
              className="rounded-full h-10 px-5 font-bold text-white text-sm inline-flex items-center gap-2" style={{ background: GOLD }}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Bell className="h-4 w-4" />} Turn on notifications
            </button>
            <button onClick={() => setShow(false)} className="text-sm font-semibold" style={{ color: "#8A6A2F" }}>Not now</button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function CountUp({ value, className, style }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-30px" });
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, value || 0, { duration: 1.1, ease: "easeOut", onUpdate: (v) => setN(Math.round(v)) });
    return () => controls.stop();
  }, [inView, value]);
  return <span ref={ref} className={className} style={style}>{n}</span>;
}

function Metric({ icon: Icon, num, label, tint, color }) {
  return (
    <div className="flex flex-col items-center">
      <div className="h-10 w-10 rounded-full flex items-center justify-center mb-2" style={{ background: tint }}>
        <Icon className="h-5 w-5" style={{ color }} />
      </div>
      <div className="font-heading font-extrabold text-3xl leading-none" style={{ color: NAVY }}><CountUp value={num} /></div>
      <div className="text-[11px] mt-1.5 leading-tight" style={{ color: "#4A5D73" }}>{label}</div>
    </div>
  );
}

function QuickRow({ icon: Icon, title, desc, onClick, testid, tint, color, faces }) {
  return (
    <motion.button whileTap={{ scale: 0.98 }} data-testid={testid} onClick={onClick} className="w-full flex items-center gap-4 bg-white rounded-2xl px-5 py-4 shadow-[0_4px_24px_rgba(13,35,64,0.04)] text-left" style={{ minHeight: 76 }}>
      <div className="h-11 w-11 rounded-full flex items-center justify-center shrink-0" style={{ background: tint }}><Icon className="h-5 w-5" style={{ color }} /></div>
      <div className="flex-1 min-w-0">
        <div className="font-heading font-bold text-lg leading-tight" style={{ color: NAVY }}>{title}</div>
        <div className="text-sm truncate" style={{ color: "#4A5D73" }}>{desc}</div>
      </div>
      {faces?.length > 0 ? (
        <div className="flex -space-x-2 shrink-0">
          {faces.slice(0, 3).map((t, i) => (
            t.photo_file_id
              ? <img key={t.id || t.name || i} src={fileUrl(`/api/files/${t.photo_file_id}`, 200)} alt="" className="rounded-full object-cover ring-2 ring-white" style={{ width: 30, height: 30 }} />
              : <div key={t.id || t.name || i} className="rounded-full flex items-center justify-center text-white text-[11px] font-bold ring-2 ring-white" style={{ width: 30, height: 30, background: AV_COLORS[i % AV_COLORS.length] }}>{initials(t.name)}</div>
          ))}
        </div>
      ) : (
        <ChevronRight className="h-5 w-5 shrink-0" style={{ color: "#94a3b8" }} />
      )}
    </motion.button>
  );
}


// Celebratory heart + confetti burst when a follower becomes a praying/giving partner.
function Celebration({ show }) {
  const hearts = 10;
  const confetti = 14;
  const colors = [GOLD, "#1E8A5B", "#0E7C86", "#C2557A", "#2563eb"];
  return (
    <AnimatePresence>
      {show && (
        <motion.div key="celebrate" data-testid="celebration-burst" className="fixed inset-0 z-[60] pointer-events-none overflow-hidden"
          initial={{ opacity: 1 }} exit={{ opacity: 0 }}>
          {[...Array(hearts)].map((_, i) => {
            const left = 12 + (i * 76) / hearts + (i % 2 ? 4 : -4);
            const delay = (i % 5) * 0.05;
            const size = 20 + (i % 4) * 6;
            return (
              <motion.div key={`h${i}`} className="absolute" style={{ left: `${left}%`, bottom: "18%" }}
                initial={{ opacity: 0, y: 0, scale: 0.4 }}
                animate={{ opacity: [0, 1, 1, 0], y: [-10, -220 - (i % 3) * 60], scale: [0.4, 1.1, 1], x: [(i % 2 ? -1 : 1) * 10, (i % 2 ? -1 : 1) * 40] }}
                transition={{ duration: 1.5, delay, ease: "easeOut" }}>
                <Heart className="drop-shadow" style={{ width: size, height: size, color: i % 3 === 0 ? "#C2557A" : GOLD, fill: i % 3 === 0 ? "#C2557A" : GOLD }} />
              </motion.div>
            );
          })}
          {[...Array(confetti)].map((_, i) => {
            const left = 8 + (i * 84) / confetti;
            return (
              <motion.span key={`c${i}`} className="absolute rounded-sm" style={{ left: `${left}%`, top: "42%", width: 7, height: 11, background: colors[i % colors.length] }}
                initial={{ opacity: 0, y: 0, rotate: 0 }}
                animate={{ opacity: [0, 1, 1, 0], y: [0, 260 + (i % 4) * 40], rotate: [0, (i % 2 ? 1 : -1) * 320], x: [0, (i % 2 ? 1 : -1) * (30 + (i % 3) * 20)] }}
                transition={{ duration: 1.6, delay: (i % 6) * 0.04, ease: "easeIn" }} />
            );
          })}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
